import _                                                from 'underscore';
import React, { CSSProperties, FC, ReactNode }          from 'react';
import classNames                                       from 'classnames';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { Icon, IconProps, Menu, MenuProps }             from 'semantic-ui-react';
import { ClassValue }                                   from 'classnames/types';

import { RouterUtility, runIfFunction } from '../../../util';
import { Align, FlexFlow, SystemEvent } from '../../../types';
import { Flexbox, FlexItem }            from '../../containers';
import { Tooltip }                      from '../util/messaging/ToolTip';
import { isFunction }                   from 'formik';
import { Cell, Grid }                   from 'styled-css-grid';


export type GenericNavItem = {
	
	/* The label to provide on nav item */
	label: ReactNode,
	
	icon?: IconProps,
	
	/* Route path to transition to */
	path: string,
	
	/* Is this nav item the parent of the items this appears with */
	isParent: boolean,
	
	/* Is this nav item the default item to go to when not specified */
	isDefault?: boolean,
	
	/* If the route requires interpolation within the string (eg: projects/:projectId),
	this object supplies those values (eg: { projectId:'12345' }) */
	params?: Record<string, unknown>,
	
	/* Contextual navigation help in the form of a tooltip */
	help?: string,
	
	/* Method to fire when a this navigation item is clicked */
	onNavigationClicked?: ( clickedItem: GenericNavItem ) => void,
	
	/* Mapping of navigational children */
	subItems?: GenericNavItem[],
	
	/* Flag active if criteria is met */
	activeIf?: () => boolean,
}

export type GenericNavComponentItem = {
	
	/* Flag denoting of the navlink is currently active */
	isActive: boolean,
	
	/* The full route of the nav link item */
	route: string,
	
	label: GenericNavItem['label'],
	icon: GenericNavItem['icon'],
	path: GenericNavItem['path'],
	help: GenericNavItem['help'],
	subItems?: GenericNavItem['subItems']
}

export interface GenericNavComponentProps extends Omit<MenuProps, 'className'> {
	
	/* The set of nav items to produce */
	navObjects: GenericNavItem[],
	
	/* Flag to include the parent item if available */
	withParent?: boolean,
	
	/* Flag to include the items include an icon */
	withIcons?: boolean,
	
	iconPosition?: Align,
	
	className?: ClassValue,
	
	/* The component a menu item should render as */
	menuItemAs?: any,
	
	/* The component a menu should render as */
	menuAs?: any,
	
	/* Classes to apply to each menu item */
	menuItemClassName?: ClassValue,
	
	/* Style to apply to each menu item */
	menuItemStyle?: CSSProperties,
	
	/* Classes to apply to each active menu item */
	menuItemActiveClassName?: ClassValue,
	
	/* Style to apply to each active menu item */
	menuItemActiveStyle?: CSSProperties,
	
	/* Method to fire when navigation is clicked */
	onNavigationClicked?: ( clickedItem: GenericNavItem ) => void,
	
	/* Delat in MS for pushing route after onNavClick executes */
	msRouteDelay?: number,
}

const defaultProps: GenericNavComponentProps = {
	navObjects:              [],
	withParent:              false,
	withIcons:               false,
	iconPosition:            Align.LEFT,
	className:               null,
	menuAs:                  Menu,
	menuItemAs:              Menu.Item,
	menuItemClassName:       null,
	menuItemStyle:           null,
	menuItemActiveClassName: null,
	menuItemActiveStyle:     null,
	onNavigationClicked:     () => {},
	msRouteDelay:            100,
}

const NavComponent: FC<GenericNavComponentProps> = ( props ) => {
	
	let history  = useHistory(),
	    location = useLocation(),
	    match    = useRouteMatch();
	
	let {
		    className,
		    navObjects,
		    menuItemClassName,
		    menuItemActiveClassName,
		    menuItemStyle,
		    menuItemActiveStyle,
		    withParent,
	    } = props;
	
	let menuProps        = _.omit( props, _.keys( defaultProps ) ),
	    containerClasses = classNames( className );
	
	// construct navItems
	let navItems = _.compact( navObjects.map( navItem => {
		
		let { pathname: routePathname } = location;
		
		let { label, icon, path, params }          = navItem,
		    { help, subItems, isParent, activeIf } = navItem,
		    route                                  = path ? RouterUtility.getRoute( path, params || {} ) : null,
		    isActive                               = !isParent && ( routePathname === route || routePathname.search( route ) !== -1 );
		
		// evaluate some criteria to make active, if applicable
		if( _.isFunction( activeIf ) ){
			
			isActive = activeIf() || isActive;
		}
		
		// craft a nav item for a component to render
		let item: GenericNavComponentItem = { isActive, route, label, icon, path, help, subItems };
		
		return isParent ? withParent ? item : null : item
		
	} ) );
	
	const handleOnNavClick = ( e: SystemEvent, route: string, navObject: GenericNavItem ) => {
		
		if( _.isFunction( props.onNavigationClicked ) || navObject.onNavigationClicked ){
			
			try{
				
				e.preventDefault();
				
				runIfFunction( props.onNavigationClicked, navObject );
				
				runIfFunction( navObject.onNavigationClicked, navObject );
				
			} catch( e ) {
				
				console.warn( `Navigation prop [ props.onNavigationClicked ] responded with error: ${ e }` );
				
			} finally {
				
				if( route ){
					
					if( props.msRouteDelay ){
						
						setTimeout( () => history.push( route ), props.msRouteDelay );
						
					} else {
						
						history.push( route );
					}
				}
			}
		}
	}
	
	let MenuContainerComponent = props.menuAs ?? defaultProps.menuAs;
	
	return (
		
		_.isFunction( props.children )
		? props.children( navItems )
		: <MenuContainerComponent className={ containerClasses } { ...menuProps }>
			
			{ navItems.map( ( { isActive, route, label, help, icon = null }, index ) => {
				
				let MenuComponent = props.menuItemAs ?? defaultProps.menuItemAs,
				    itemClasses   = classNames( menuItemClassName, isActive ? menuItemActiveClassName : null ),
				    handleClick   = _.partial( handleOnNavClick, _, route, navObjects[ index ] );
				
				return (
					
					<MenuComponent active={ isActive } key={ `${ route }-${ label }` } onMouseUp={ handleClick }>
						
						<Link to={ route ?? location }
						      onClick={ handleClick }
						      className={ itemClasses }
						      style={ isActive ? menuItemActiveStyle : menuItemStyle }>
							
							{ ( props.withIcons && !_.isNull( icon ) ) ? (
								
								<Grid columns={ `minmax(25px, auto) 1fr` } areas={ [ 'a b' ] } columnGap={ '5px' } rowGap={ '0px' }>
									<Cell center middle area={ props.iconPosition === Align.RIGHT ? 'b' : 'a' }><Icon fitted { ...icon } /></Cell>
									<Cell middle area={ props.iconPosition === Align.RIGHT ? 'a' : 'b' }>{ label }{ help && <Tooltip content={ help }/> }</Cell>
								</Grid>
								
								/*<Flexbox direction={ props.iconPosition === Align.RIGHT ? FlexFlow.ROW_REVERSE : null }>
									<FlexItem noGrow><Icon fitted { ...icon } /></FlexItem>
									<FlexItem className={ classNames( {
										'margin-sm-right': props.iconPosition === Align.RIGHT,
										'margin-sm-left':  props.iconPosition === Align.LEFT,
									} ) } basis={ 50 }>{ label }{ help && <Tooltip content={ help }/> }</FlexItem>
								</Flexbox>*/
							
							) : label }
						
						</Link>
					
					</MenuComponent>
				)
				
			} ) }
		
		</MenuContainerComponent>
	);
};

NavComponent.defaultProps = defaultProps;

export { NavComponent as GenericNav }
